import type { SkillId } from './skills';

export interface SkillBoard {
  id: SkillId;
  title: string;
  subtitle: string;
  description: string;
  /** Premium accent (muted metal / champagne — not neon) */
  color: string;
  icon: string;
  progress: number;
  skills: string[];
  wall: 'left' | 'right';
  /** Distance along tunnel from entrance (meters toward exit). */
  z: number;
}

/**
 * Symmetrical board spacing: each board sits exactly halfway between two floor kick lights.
 */
export const BOARD_Z0 = 5.5;
export const BOARD_SPACING = 2.8;

export const skillBoards: SkillBoard[] = [
  {
    id: 'ai',
    title: 'AI & LLM',
    subtitle: 'ENGINEERING',
    description:
      'Building intelligent systems with LLMs, agents, and advanced AI workflows.',
    color: '#C9A86C',
    icon: '🧠',
    progress: 88,
    skills: [
      'LangChain',
      'LangGraph',
      'RAG',
      'MCP',
      'Ollama',
      'n8n',
    ],
    wall: 'right',
    z: BOARD_Z0,
  },
  {
    id: 'cloud',
    title: 'Cloud &',
    subtitle: 'DEVOPS',
    description: 'Ship, scale, and automate with cloud-native tooling.',
    color: '#B89B6A',
    icon: '☁️',
    progress: 78,
    skills: [
      'Azure',
      'AWS',
      'Docker',
      'Kubernetes',
      'CI/CD',
      'GitHub Actions',
    ],
    wall: 'left',
    z: BOARD_Z0 + BOARD_SPACING,
  },
  {
    id: 'frontend',
    title: 'Frontend',
    subtitle: 'ENGINEERING',
    description:
      'Polished interfaces with modern React architecture and motion.',
    color: '#A68B5B',
    icon: '💻',
    progress: 90,
    skills: [
      'React',
      'Next.js',
      'TypeScript',
      'Tailwind',
      'Redux',
      'Responsive UI',
    ],
    wall: 'right',
    z: BOARD_Z0 + BOARD_SPACING * 2,
  },
  {
    id: 'ml',
    title: 'Machine',
    subtitle: 'LEARNING',
    description:
      'Predictive models, explainability, and production-grade ML pipelines.',
    color: '#9C7A52',
    icon: '📈',
    progress: 84,
    skills: [
      'XGBoost',
      'Scikit-learn',
      'SHAP',
      'Feature Engineering',
      'Model Evaluation',
      'Predictive Models',
    ],
    wall: 'left',
    z: BOARD_Z0 + BOARD_SPACING * 3,
  },
  {
    id: 'three',
    title: 'Three.js',
    subtitle: 'CREATIVE',
    description: 'Immersive 3D experiences with real-time graphics.',
    color: '#D4AF37',
    icon: '🎮',
    progress: 76,
    skills: ['Three.js', 'R3F', 'Drei', 'GSAP', 'GLSL', 'Rapier'],
    wall: 'right',
    z: BOARD_Z0 + BOARD_SPACING * 4,
  },
  {
    id: 'backend',
    title: 'Backend',
    subtitle: 'ENGINEERING',
    description: 'APIs, services, and reliable server-side systems.',
    color: '#8B7355',
    icon: '⚙️',
    progress: 86,
    skills: ['Node.js', 'Express', 'Django', 'FastAPI', 'Judge0', 'GraphQL'],
    wall: 'left',
    z: BOARD_Z0 + BOARD_SPACING * 5,
  },
  {
    id: 'database',
    title: 'Databases',
    subtitle: '& SEARCH',
    description: 'Structured, cache, and vector data for modern apps.',
    color: '#C4B08A',
    icon: '🗄️',
    progress: 82,
    skills: [
      'MongoDB',
      'MySQL',
      'Redis',
      'Qdrant',
      'Vector DB',
      'Semantic Search',
    ],
    wall: 'right',
    z: BOARD_Z0 + BOARD_SPACING * 6,
  },
  {
    id: 'architecture',
    title: 'System',
    subtitle: 'ARCHITECTURE',
    description: 'Scalable design patterns for production systems.',
    color: '#E6D5B8',
    icon: '🏗',
    progress: 80,
    skills: [
      'Microservices',
      'Scalability',
      'API Design',
      'Concurrency',
      'Optimization',
      'SDLC',
    ],
    wall: 'left',
    z: BOARD_Z0 + BOARD_SPACING * 7,
  },
];
